import { useState } from 'react';
import { useFragment } from '@apollo/client/react';
import { isEmpty } from 'lodash';

import { createRequiredContext } from 'src/context/createRequiredContext';
import { BeneficiaryFields } from 'src/graphql/actions/beneficiary/beneficiary.fragment';
import { AgreementFormEnum, type IBeneficiary } from 'src/graphql/schema';

export type IBeneficiaryMode = 'select' | 'create' | 'view' | 'edit';

const [useBeneficiaryContext, BeneficiaryContext] = createRequiredContext<{
  beneficiary: IBeneficiary | null;
  escrowId: string;
  agreementType: AgreementFormEnum;
  canCreateBeneficiary: boolean;
  canChangeBeneficiary: boolean;
  mode: IBeneficiaryMode;
  setMode: (mode: IBeneficiaryMode) => void;
}>();

type Props = {
  beneficiaryId: string;
  escrowId: string;
  agreementType: AgreementFormEnum;
  canCreateBeneficiary: boolean;
  canChangeBeneficiary: boolean;
};

const BeneficiaryContextProvider = ({
  beneficiaryId,
  escrowId,
  agreementType,
  canCreateBeneficiary,
  canChangeBeneficiary,
  children,
}: React.PropsWithChildren<Props>) => {
  const { data } = useFragment<IBeneficiary>({
    fragment: BeneficiaryFields,
    fragmentName: 'BeneficiaryFields',
    from: {
      __typename: 'Beneficiary',
      id: beneficiaryId || '',
    },
  });

  const beneficiary = isEmpty(data) ? null : (data as IBeneficiary);
  const [mode, setMode] = useState<IBeneficiaryMode>(beneficiary ? 'view' : 'select');

  const value = {
    beneficiary,
    escrowId,
    agreementType,
    canCreateBeneficiary,
    canChangeBeneficiary,
    mode,
    setMode,
  };

  return <BeneficiaryContext value={value}>{children}</BeneficiaryContext>;
};

export { useBeneficiaryContext, BeneficiaryContextProvider };
